import { z } from 'zod';
import { Email, IsoDateTime, Uuid, patchOf } from './common.js';
import { SiteAlertState } from './evacuation.js';
/**
 * Modules `contactless` and `sign-in-points` (docs/modules/contactless.md, sign-in-points.md, ADR 0022). Owner: task S24.
 * Builders may add optional fields; renaming or removing a field needs the architect.
 */
// ---------- Portal: per-site set-up ----------
export const ContactlessSite = z.object({
    siteId: Uuid,
    enabled: z.boolean(),
    qrMode: z.enum(['static', 'rotating']),
    /** The printed link (static mode); null in rotating mode. */
    url: z.string().nullable(),
    linkVersion: z.number().int(),
});
/** POST /contactless/sites/:siteId/new-link: revokes the printed QR and returns the new one. */
export const ContactlessNewLink = z.object({ url: z.string(), linkVersion: z.number().int() });
/** GET /contactless/device/rotating-qr (kiosk or display in rotating mode). */
export const RotatingQr = z.object({ url: z.string(), expiresAt: IsoDateTime, refreshAfterSeconds: z.number().int() });
// ---------- Public page ----------
/** GET /contactless/public/:token: the start page (no session yet). */
export const ContactlessStart = z.object({
    siteName: z.string(),
    tenantName: z.string(),
    branding: z.object({ logoUrl: z.string().nullable(), accent: z.string().nullable() }),
    /** From a sign-in point: what the point does. */
    point: z.object({ name: z.string(), mode: z.enum(['in', 'out', 'both']) }).nullable(),
    requireNearSite: z.boolean(),
});
export const ContactlessSessionCreate = z.object({
    /** The remember-me token from localStorage, if any. */
    rememberToken: z.string().max(400).nullable().default(null),
    /** Only when the site asks (requireNearSite); never stored. */
    location: z.object({ lat: z.number(), lng: z.number(), accuracyM: z.number().min(0).max(10000) }).nullable().default(null),
});
export const ContactlessSession = z.object({
    /** Shown once; the page keeps it in sessionStorage and sends `Authorization: Contactless <token>`. */
    token: z.string(),
    expiresAt: IsoDateTime,
    remembered: z.object({ fullName: z.string(), company: z.string(), email: z.string() }).nullable(),
    /** The visit this browser has open at the site (from the remember token), for signing out. */
    openVisit: z.object({ visitId: Uuid, signedInAt: IsoDateTime, groupName: z.string() }).nullable(),
});
/** A group as the phone shows it, with its steps (same keys and configs as the kiosk, minus badge). */
export const ContactlessGroup = z.object({
    id: Uuid,
    name: z.string(),
    kind: z.enum(['standard', 'repeat']),
    welcomeMessage: z.string(),
    signInSteps: z.array(z.object({ key: z.string(), order: z.number().int(), config: z.unknown() })),
    signOutSteps: z.array(z.object({ key: z.string(), order: z.number().int(), config: z.unknown() })),
    requireFullName: z.boolean(),
});
export const ContactlessConfig = z.object({
    siteId: Uuid,
    siteName: z.string(),
    timeZone: z.string(),
    groups: z.array(ContactlessGroup),
    /** Sections other modules add (safety, compliance, induction, capacity), as for the kiosk. */
    sections: z.record(z.string(), z.unknown()),
});
export const ContactlessSignIn = z.object({
    idempotencyKey: Uuid,
    groupId: Uuid,
    visitorName: z.string().trim().min(1).max(120),
    email: Email.nullable().default(null),
    company: z.string().trim().max(120).default(''),
    hostMemberId: Uuid.nullable().default(null),
    steps: z.record(z.string(), z.unknown()).default({}),
    remember: z.boolean().default(false),
});
export const ContactlessSignInResult = z.object({
    status: z.enum(['applied', 'merged', 'refused']),
    visitId: Uuid.nullable(),
    messageKey: z.string().nullable(),
    /** Set when remember was ticked; keep in localStorage. */
    rememberToken: z.string().nullable(),
    thankYouMessage: z.string(),
});
export const ContactlessSignOut = z.object({ idempotencyKey: Uuid, steps: z.record(z.string(), z.unknown()).default({}) });
/** GET /contactless/session/state, polled every 10 s: evacuation state for this site (no names). */
export const ContactlessState = z.object({ alert: SiteAlertState, canMarkSafe: z.boolean(), markedSafeAt: IsoDateTime.nullable() });
/** DELETE /contactless/public/remember: forget this phone. */
export const ContactlessForget = z.object({ rememberToken: z.string().max(400) });
export const ContactlessTenantSettings = z.object({
    rememberDays: z.number().int().min(0).max(365).default(90),
    sessionMinutes: z.number().int().min(5).max(120).default(30),
});
export const ContactlessSiteSettings = z.object({
    enabled: z.boolean().default(false),
    qrMode: z.enum(['static', 'rotating']).default('static'),
    maxSessionsPerHour: z.number().int().min(0).default(300),
    requireNearSite: z.boolean().default(false),
    /** Visitors on a phone may search for their host by name. Off: the host box is not offered. */
    hostSearch: z.boolean().default(true),
    /** Off (default): the search shows first name and surname initial only ("Sam P."). On: full names. */
    hostSearchFullNames: z.boolean().default(false),
});
export const ContactlessGroupSettings = z.object({
    showOnContactless: z.boolean().default(true),
});
// ---------- Sign-in points ----------
export const SignInPointMode = z.enum(['in', 'out', 'both']);
export const SignInPoint = z.object({
    id: Uuid,
    siteId: Uuid,
    name: z.string(),
    mode: SignInPointMode,
    /** Restrict to these groups; null = every group shown at the site. */
    groupIds: z.array(Uuid).nullable(),
    /** Phase 5 (Spaces): floor or zone this point counts people for. */
    zoneId: Uuid.nullable(),
    active: z.boolean(),
    url: z.string(),
    linkVersion: z.number().int(),
    createdAt: IsoDateTime,
});
export const SignInPointCreate = z.object({
    siteId: Uuid,
    name: z.string().trim().min(1).max(80),
    mode: SignInPointMode.default('both'),
    groupIds: z.array(Uuid).max(200).nullable().default(null),
});
export const SignInPointUpdate = patchOf(SignInPointCreate.omit({ siteId: true })).extend({ active: z.boolean().optional() });
/** Companion app: POST /sign-in-points/me/scan with the scanned link. */
export const SignInPointScan = z.object({
    idempotencyKey: Uuid,
    url: z.string().max(600),
    occurredAt: IsoDateTime,
    location: z.object({ lat: z.number(), lng: z.number(), accuracyM: z.number() }).nullable().default(null),
});
export const SignInPointScanResult = z.object({
    action: z.enum(['signed_in', 'signed_out', 'refused']),
    visitId: Uuid.nullable(),
    siteName: z.string(),
    messageKey: z.string().nullable(),
});
export const SignInPointsTenantSettings = z.object({
    /** Points printed as A4 posters: show the tenant logo. */
    posterShowsLogo: z.boolean().default(true),
});
export const SignInPointsSiteSettings = z.object({ enabled: z.boolean().default(true) });
// ---------- Added by S24 ----------
/** GET /contactless/session/hosts?q=: names only, at most 10 (min 2 letters). */
export const ContactlessHostQuery = z.object({ q: z.string().trim().min(2).max(80) });
export const ContactlessHostResults = z.object({ items: z.array(z.object({ id: Uuid, fullName: z.string() })).max(10) });
/** POST /visits/contactless/uploads: a photo (JPEG), its thumbnail or a signature (strokes JSON). The page sends the returned id in its step answers. */
export const ContactlessUploadKind = z.enum(['photo', 'photo_thumb', 'signature']);
export const ContactlessUploadResult = z.object({ uploadId: Uuid, fileId: Uuid });
//# sourceMappingURL=contactless.js.map